// src/pages/accounting/MarkupFactorPage.jsx
// Extracted from App.js - Markup Factor admin page
// BRIO Migration: Using code "2c" (list/card variant)
//
// ============================================================================
// SETTINGS REFACTOR (Feb 2026):
// - Removed all settings editing controls (essentialProfitPercentage input,
//   manualSalaryMarkup input, "Clear Manual Override" button).
// - This page is now READ-ONLY for settings. All settings edits happen in
//   AccountingSettingsPage via useSettings hook.
// - Replaced `settings` / `setSettings` props with `settingsHook` (useSettings).
// - Removed direct Supabase writes (supabaseSalarySettings.upsert/clear).
// ============================================================================

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioTable,
  BrioByCode,
  getTablePropsFromCode,
  useFullScreenPage,
  BrioFullScreenButton
} from '../../components/BrioComponents';
import { useSalaryLedger } from '../../hooks/useSalaryLedger';

const START_YEAR = 2018;

// ── Combined reference panel (formula, usage, definitions) ──
function ReferencePanel({ currSymbol, essentialProfitPercentage, totalFixedSalaries, officeOverheads, getEffectiveSalaryMarkup, currentYear, manualSalaryMarkup }) {
  const [open, setOpen] = useState(false);
  const effectiveMarkup = getEffectiveSalaryMarkup(currentYear);

  return (
    <BrioAnalyticsSurface padding="m">
      <button
        onClick={() => setOpen(v => !v)}
        className="brio-flex brio-items-center brio-justify-between"
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
      >
        <div className="brio-font-medium" style={{ fontSize: 13 }}>Reference: Formula, Usage &amp; Definitions</div>
        <span style={{ fontSize: 12, color: 'var(--brio-grey)' }}>{open ? '▲ collapse' : '▼ expand'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Formula */}
          <div style={{ background: 'var(--brio-gold-light)', padding: '12px 14px', border: '1px solid var(--brio-gold)' }}>
            <div className="brio-font-medium brio-mb-xs" style={{ fontSize: 12 }}>Formula</div>
            <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
              Salary Markup Factor = (Total Salary Cost + Office Overheads + Essential Profit) / Total Salary Cost
            </div>
            <div className="brio-analytics-muted brio-mt-xs brio-italic" style={{ fontSize: 11, lineHeight: '1.5' }}>
              Total Salary Cost = all employee base salaries + bonuses (incl. MGMT &amp; Support).{' '}
              Office Overheads = rent, utilities, insurance, etc.{' '}
              Project-specific expenses are not included here — they are tracked as separate line items on cost/profitability pages.
            </div>
          </div>

          {/* What the active markup means */}
          <div style={{ padding: '12px 14px', border: '1px solid var(--brio-border, #e5e5e5)' }}>
            <div className="brio-font-medium brio-mb-xs" style={{ fontSize: 12 }}>What the active markup ({effectiveMarkup.toFixed(3)}x) means</div>
            <div style={{ fontSize: 12 }}>
              For every {currSymbol}1 of salary cost, generate {currSymbol}{effectiveMarkup.toFixed(2)} to cover the salary itself ({currSymbol}1.00),
              overheads ({currSymbol}{(totalFixedSalaries > 0 ? officeOverheads / totalFixedSalaries : 0).toFixed(2)} per {currSymbol}1), and
              the {essentialProfitPercentage}% profit target.
            </div>
            {manualSalaryMarkup[currentYear] && (
              <div className="brio-mt-xs brio-font-medium" style={{ fontSize: 11, color: 'var(--brio-orange)' }}>
                Currently using manual override. Clear it in Settings to use calculated value.
              </div>
            )}
          </div>

          {/* How to use + Internal vs Client distinction */}
          <div style={{ padding: '12px 14px', border: '1px solid var(--brio-border, #e5e5e5)', fontSize: 12 }}>
            <div className="brio-font-medium brio-mb-xs" style={{ fontSize: 12 }}>How to use</div>
            <div style={{ lineHeight: '1.6' }}>
              <strong>Beginning of year:</strong> enter projected manual markup in Settings.{' '}
              <strong>During year:</strong> adjust as plans change.{' '}
              <strong>Year-end:</strong> review calculated vs manual and decide which to keep.{' '}
              No manual entry = system uses calculated value.
            </div>
            <div className="brio-mt-s" style={{ paddingTop: 8, borderTop: '1px solid var(--brio-border, #e5e5e5)' }}>
              <strong>Internal Cost Rate</strong> = Monthly Salary x Markup / 176 hrs (for profitability tracking).{' '}
              <strong>Client Billing Rate</strong> is independent — set by market rates, not internal costs.
            </div>
          </div>
        </div>
      )}
    </BrioAnalyticsSurface>
  );
}

export default function MarkupFactorPage({
  // Data
  activeUsers,
  activeOfficeOverheads,
  // Settings (read-only from useSettings hook)
  settingsHook,
  // Form state
  form,
  // Functions
  getEffectiveSalaryMarkup,
  getEffectiveSalary,
  getInternalCostRate,
  calculateSalaryMarkupFactor, // eslint-disable-line no-unused-vars
  computeOverheadAllocation, // eslint-disable-line no-unused-vars
  // Formatters
  fmtCurr,
  currSymbol = '₹',
  // Navigation (for "Go to Settings" link)
  setMainCategory,
  setSubPage,
  // RBAC
  isAdmin,
}) {
  // Read settings values (read-only)
  const essentialProfitPercentage = settingsHook?.essentialProfitPercentage ?? 20;
  const manualSalaryMarkup = settingsHook?.manualSalaryMarkup ?? {};
  const tableProps = getTablePropsFromCode('1b');

  // ── Salary ledger: fetch all rows for START_YEAR through current year ──
  const { getAllForYear } = useSalaryLedger();
  const currentCalendarYear = new Date().getFullYear();

  // ledgerByYear: { [year]: [ { auth_user_id, month, base_salary, bonus, ... }, ... ] }
  const [ledgerByYear, setLedgerByYear] = useState({});
  const fetchAllLedgerData = useCallback(async () => {
    const result = {};
    // Fetch all years in parallel
    const promises = [];
    for (let y = START_YEAR; y <= currentCalendarYear; y++) {
      promises.push(getAllForYear(y).then(rows => ({ year: y, rows })));
    }
    const results = await Promise.all(promises);
    for (const { year, rows } of results) {
      result[year] = rows || [];
    }
    setLedgerByYear(result);
  }, [getAllForYear, currentCalendarYear]);

  useEffect(() => { fetchAllLedgerData(); }, [fetchAllLedgerData]);

  // Helper: compute total annual salaries for a given year from ledger (matching SalaryCostPage)
  const getTotalAnnualSalaries = useCallback((year) => {
    const ledgerRows = ledgerByYear[year] || [];

    if (ledgerRows.length > 0) {
      // Sum base_salary + bonus for every user-month row in the ledger for this year
      return ledgerRows.reduce((sum, row) => {
        return sum + (Number(row.base_salary) || 0) + (Number(row.bonus) || 0);
      }, 0);
    }

    // No ledger data for this year — try getInternalCostRate (salary matrix fallback)
    if (getInternalCostRate) {
      return activeUsers.reduce((sum, user) => {
        let annualTotal = 0;
        for (let m = 1; m <= 12; m++) {
          const rate = getInternalCostRate(user.id, year, m);
          if (rate) {
            annualTotal += (rate.monthlySalary || 0) + (rate.monthlyBonusEquivalent || 0);
          }
        }
        return sum + annualTotal;
      }, 0);
    }

    // Last resort: getEffectiveSalary (legacy)
    if (getEffectiveSalary) {
      return activeUsers.reduce((sum, user) => {
        let annualTotal = 0;
        for (let m = 1; m <= 12; m++) {
          annualTotal += getEffectiveSalary(user.id, year, m);
        }
        return sum + annualTotal;
      }, 0);
    }

    return 0;
  }, [ledgerByYear, activeUsers, getInternalCostRate, getEffectiveSalary]);

  // ── Multi-year markup table (FY 2018 → current year) ──
  const yearlyMarkupData = useMemo(() => {
    const years = [];
    for (let y = START_YEAR; y <= currentCalendarYear; y++) {
      const officeOverheads = activeOfficeOverheads
        .filter(o => o.year === y)
        .reduce((sum, o) => sum + (o.amount || 0), 0);
      const totalFixedSalaries = getTotalAnnualSalaries(y);
      // Markup uses office overheads only (project expenses are separate line items)
      const targetRevenue = totalFixedSalaries + officeOverheads;
      const essentialProfit = targetRevenue * (essentialProfitPercentage / 100);
      const calculated = totalFixedSalaries > 0
        ? (totalFixedSalaries + officeOverheads + essentialProfit) / totalFixedSalaries
        : 1;
      const manual = manualSalaryMarkup[y] || null;
      const effective = getEffectiveSalaryMarkup(y);
      years.push({
        year: y, totalFixedSalaries,
        officeOverheads,
        essentialProfit, calculated, manual, effective,
      });
    }
    return years.reverse(); // Most recent first
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOfficeOverheads, essentialProfitPercentage, manualSalaryMarkup, getEffectiveSalaryMarkup, getTotalAnnualSalaries, currentCalendarYear]);

  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);

  return (
    <div className={`brio-flex brio-flex-col brio-analytics-font ${rootClassName}`} style={{ background: '#fff', ...rootStyle }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">
              <h3 className="brio-analytics-section-title" style={{ fontSize: '20px', fontWeight: 700 }}>Salary Markup Factor (Yearly)</h3>
              <p className="brio-analytics-muted brio-mt-xs">Universal multiplier applied to all salaries for billing rate calculation</p>
            </div>
            <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} />
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {(() => {
            const currentYear = form.overheadYear || new Date().getFullYear();
            const totalFixedSalaries = getTotalAnnualSalaries(currentYear);
            const officeOverheads = activeOfficeOverheads
              .filter(o => o.year === currentYear)
              .reduce((sum, o) => sum + (o.amount || 0), 0);
            // Markup uses office overheads only — project expenses are
            // added as separate line items on cost/profitability pages
            // to avoid double-counting.
            const targetRevenue = totalFixedSalaries + officeOverheads;
            const essentialProfit = targetRevenue * (essentialProfitPercentage / 100);
            const salaryMarkupFactor = totalFixedSalaries > 0
              ? (totalFixedSalaries + officeOverheads + essentialProfit) / totalFixedSalaries
              : 1;

            return (
              <>
                {/* Settings notice (admin only) */}
                {isAdmin && setMainCategory && (
                  <BrioAnalyticsSurface padding="s">
                    <div className="brio-flex brio-items-center brio-justify-between" style={{ fontSize: '12px', color: 'var(--brio-grey)' }}>
                      <span style={{ fontStyle: 'italic' }}>Settings (profit %, manual markup) are managed centrally.</span>
                      <button
                        onClick={() => { setMainCategory('accounting'); setSubPage('settings'); }}
                        className="brio-btn brio-btn-secondary"
                        style={{ fontSize: '11px', padding: '4px 10px' }}
                      >
                        Accounting → Settings
                      </button>
                    </div>
                  </BrioAnalyticsSurface>
                )}

                {/* Reference: Formula, Usage & Definitions */}
                <ReferencePanel
                  currSymbol={currSymbol}
                  essentialProfitPercentage={essentialProfitPercentage}
                  totalFixedSalaries={totalFixedSalaries}
                  officeOverheads={officeOverheads}
                  getEffectiveSalaryMarkup={getEffectiveSalaryMarkup}
                  currentYear={currentYear}
                  manualSalaryMarkup={manualSalaryMarkup}
                />

                {/* Calculation Breakdown - Card Grid (code 2c pattern) */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <BrioAnalyticsSurface padding="s">
                    <div className="brio-analytics-muted brio-mb-xs">Year</div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>{currentYear}</div>
                  </BrioAnalyticsSurface>

                  <BrioAnalyticsSurface padding="s">
                    <div className="brio-analytics-muted brio-mb-xs">Essential Profit Target</div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>
                      {essentialProfitPercentage}%
                    </div>
                    <div className="brio-text-xs brio-text-grey brio-italic brio-mt-xs">Read-only — edit in Settings</div>
                  </BrioAnalyticsSurface>

                  <BrioAnalyticsSurface padding="s">
                    <div className="brio-analytics-muted brio-mb-xs">Total Salary Cost (Annual)</div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>{fmtCurr(totalFixedSalaries)}</div>
                    <div className="brio-text-xs brio-text-grey brio-mt-xs" style={{ lineHeight: '1.5' }}>
                      All employee base salaries + bonuses
                    </div>
                  </BrioAnalyticsSurface>

                  <BrioAnalyticsSurface padding="s">
                    <div className="brio-analytics-muted brio-mb-xs">Office Overheads (Annual)</div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>{fmtCurr(officeOverheads)}</div>
                    <div className="brio-text-xs brio-text-grey brio-mt-xs" style={{ lineHeight: '1.5' }}>
                      Rent, utilities, insurance, etc.
                    </div>
                  </BrioAnalyticsSurface>

                  <BrioAnalyticsSurface padding="s">
                    <div className="brio-analytics-muted brio-mb-xs">Essential Profit</div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>{fmtCurr(essentialProfit)}</div>
                    <div className="brio-text-xs brio-text-grey brio-italic brio-mt-xs">
                      {essentialProfitPercentage}% of ({fmtCurr(totalFixedSalaries)} + {fmtCurr(officeOverheads)})
                    </div>
                  </BrioAnalyticsSurface>

                  <BrioAnalyticsSurface padding="s">
                    <div className="brio-analytics-muted brio-mb-xs">Calculated Salary Markup Factor</div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: 'var(--brio-grey)' }}>
                      {salaryMarkupFactor.toFixed(3)}×
                    </div>
                    <div className="brio-text-xs brio-text-grey brio-italic brio-mt-xs">
                      Based on formula (reference only)
                    </div>
                  </BrioAnalyticsSurface>

                  <BrioAnalyticsSurface padding="s" style={{ gridColumn: 'span 2', borderColor: 'var(--brio-gold)', borderWidth: '2px', background: 'var(--brio-gold-light)' }}>
                    <div className="brio-analytics-muted brio-mb-xs">
                      Manual Salary Markup Factor
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: 'var(--brio-gold)' }}>
                      {manualSalaryMarkup[currentYear]
                        ? `${manualSalaryMarkup[currentYear]}×`
                        : '—'}
                    </div>
                    <div className="brio-text-xs brio-text-grey brio-italic brio-mt-xs">
                      {manualSalaryMarkup[currentYear] 
                        ? `Overriding calculated (${salaryMarkupFactor.toFixed(3)}×) — edit in Settings` 
                        : 'Not set — using calculated value. Set in Settings'
                      }
                    </div>
                  </BrioAnalyticsSurface>
                </div>

                {/* Active Markup Display */}
                <BrioAnalyticsSurface padding="m" style={{ borderColor: 'var(--brio-gold)', borderWidth: '2px', background: 'var(--brio-gold-light)' }}>
                  <div className="brio-flex brio-items-center brio-justify-between">
                    <div>
                      <div className="brio-analytics-muted">Active Markup for {currentYear} (used in calculations)</div>
                      <div style={{ fontSize: '28px', fontWeight: 600, color: 'var(--brio-gold)', marginTop: '4px' }}>
                        {getEffectiveSalaryMarkup(currentYear).toFixed(3)}×
                      </div>
                    </div>
                  </div>
                </BrioAnalyticsSurface>

                {/* Multi-Year Markup Table */}
                <BrioAnalyticsSurface padding="m">
                  <div className="brio-font-medium brio-mb-xs" style={{ fontSize: 14 }}>Yearly Markup Factor ({START_YEAR}–{currentCalendarYear})</div>
                  <p className="brio-analytics-muted brio-text-xs" style={{ marginBottom: 12 }}>
                    Salary Cost = all employee base salaries + bonuses. Overheads = office overheads only (project expenses are tracked separately on cost/profitability pages).
                  </p>
                  <BrioByCode code="1b">
                    <BrioTable {...tableProps}>
                      <thead>
                        <tr>
                          <th>FY</th>
                          <th className="text-right">Salary Cost</th>
                          <th className="text-right">Overheads</th>
                          <th className="text-right">Essential Profit</th>
                          <th className="text-right">Calculated</th>
                          <th className="text-right" style={{ color: 'var(--brio-gold)' }}>Manual</th>
                          <th className="text-right" style={{ fontWeight: 700 }}>Effective</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yearlyMarkupData.map(row => {
                          const hasData = row.officeOverheads > 0 || row.totalFixedSalaries > 0;
                          return (
                            <tr key={row.year} className="brio-row--hoverable" style={row.year === currentYear ? { background: 'var(--brio-gold-light)' } : undefined}>
                              <td className="brio-font-medium">{row.year}</td>
                              <td className="text-right brio-text-grey" title={`All salaries + bonuses`}>
                                {fmtCurr(row.totalFixedSalaries)}
                              </td>
                              <td className="text-right brio-text-grey" style={!hasData ? { color: '#ccc', fontStyle: 'italic' } : undefined} title="Office overheads (rent, utilities, etc.)">
                                {hasData ? fmtCurr(row.officeOverheads) : '—'}
                              </td>
                              <td className="text-right brio-text-grey">
                                {hasData ? fmtCurr(row.essentialProfit) : '—'}
                              </td>
                              <td className="text-right" style={{ color: 'var(--brio-grey)' }}>
                                {hasData ? `${row.calculated.toFixed(3)}×` : '—'}
                              </td>
                              <td className="text-right" style={{ color: 'var(--brio-gold)', fontWeight: 600 }}>
                                {row.manual ? `${row.manual}×` : '—'}
                              </td>
                              <td className="text-right brio-font-medium" style={{ fontWeight: 700 }}>
                                {row.effective > 0 ? `${row.effective.toFixed(3)}×` : <span style={{ color: '#c0392b' }}>0</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </BrioTable>
                  </BrioByCode>
                </BrioAnalyticsSurface>

              </>
            );
          })()}
        </div>
      </BrioFrame>
    </div>
  );
}
